import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { speakDevice, stopDevice } from "@/lib/deviceVoice";
import { useNavigate } from "react-router-dom";
import { GARDIENS, COLLECTIONS, POSTER, POSTERS } from "@/data/gardiens";
import { LOGO } from "@/data/exercises";
import { Button } from "@/components/ui/button";
import { ArrowLeft, X, Volume2, Loader2 } from "lucide-react";

function GardienCard({ g, onOpen }) {
  return (
    <button onClick={() => onOpen(g)} data-testid={`gardien-card-${g.id}`}
      className="group text-left bg-card rounded-3xl border border-border/60 p-6 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-[transform,box-shadow] duration-500">
      <div className="relative mb-5">
        <img src={g.image} alt={g.name} className="w-full aspect-[4/3] object-cover object-top rounded-2xl" />
        {g.familiar && <img src={g.familiar.image} alt={g.familiar.name} className="absolute -bottom-4 right-4 w-16 h-16 rounded-full object-cover border-4 border-card shadow-md" />}
      </div>
      <p className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: g.color }}>{g.value}</p>
      <h3 className="font-serif-display text-2xl mt-1">{g.name}</h3>
      <p className="text-sm text-muted-foreground">{g.title}</p>
      <p className="font-serif-display italic text-foreground/75 mt-3 leading-snug">« {g.quote} »</p>
      {g.familiar && <p className="text-sm text-warm mt-3">Avec {g.familiar.name}, {g.familiar.title}</p>}
    </button>
  );
}

function useGardienIntro(g) {
  const gid = g.id;
  const [state, setState] = useState("loading");
  const audio = useRef(null);
  const introText = `Je suis ${g.name}, ${(g.title || "").charAt(0).toLowerCase()}${(g.title || "").slice(1)}. ${g.quote || ""}`;
  const speakIntro = () => speakDevice(introText, { onStart: () => setState("playing"), onEnd: () => setState("ready") }) || setState("none");
  useEffect(() => {
    let alive = true;
    api.get(`/gardiens/${gid}/intro`).then(({ data }) => {
      if (!alive) return;
      if (!data.audio_url) { setState("ready"); speakIntro(); return; }
      const a = new Audio(`${process.env.REACT_APP_BACKEND_URL}${data.audio_url}`);
      audio.current = a;
      a.onplay = () => setState("playing");
      a.onended = a.onpause = () => setState("ready");
      a.play().catch(() => setState("ready"));
    }).catch(() => { if (alive) { setState("ready"); speakIntro(); } });
    return () => { alive = false; audio.current?.pause(); stopDevice(); };
  }, [gid]); // eslint-disable-line react-hooks/exhaustive-deps
  const replay = () => { const a = audio.current; if (a) { a.currentTime = 0; a.play().catch(() => {}); } else speakIntro(); };
  return { state, replay };
}

function GardienDetail({ g, onClose }) {
  const { state, replay } = useGardienIntro(g);
  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="gardien-detail">
      <div className="soft-in bg-background rounded-[2rem] max-w-lg w-full max-h-[90vh] overflow-y-auto p-7 sm:p-9 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="gardien-detail-close" className="absolute top-4 right-4 w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-accent transition-colors" aria-label="Fermer">
          <X className="w-5 h-5" />
        </button>
        <img src={g.image} alt={g.name} className="w-32 h-32 rounded-full object-cover object-top border-4 mx-auto" style={{ borderColor: `${g.color}66` }} />
        <p className="text-center text-xs uppercase tracking-[0.2em] mt-5 font-semibold" style={{ color: g.color }}>{g.value}</p>
        <h2 className="text-center font-serif-display text-3xl mt-1">{g.name}</h2>
        <p className="text-center text-muted-foreground">{g.title}</p>
        <p className="text-center font-serif-display italic text-xl text-foreground/80 mt-4">« {g.quote} »</p>
        {state !== "none" && (
          <div className="flex justify-center mt-4">
            <button onClick={replay} disabled={state === "loading"} data-testid="gardien-detail-listen"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full border-2 font-semibold transition-colors disabled:opacity-60"
              style={{ borderColor: `${g.color}66`, color: g.color }}>
              {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className={`w-4 h-4 ${state === "playing" ? "animate-pulse" : ""}`} />}
              {state === "loading" ? `${g.name} prend la parole…` : state === "playing" ? `${g.name} vous parle…` : `Réécouter ${g.name}`}
            </button>
          </div>
        )}
        {g.story && <p className="text-center text-foreground/70 mt-4 leading-relaxed" data-testid="gardien-detail-story">{g.story}</p>}
        {g.familiar && (
          <div className="mt-6 flex items-center gap-4 bg-warm/10 border border-warm/30 rounded-2xl p-4" data-testid="gardien-detail-familiar">
            <img src={g.familiar.image} alt={g.familiar.name} className="w-20 h-20 rounded-full object-cover shrink-0" style={{ boxShadow: `0 0 0 3px ${g.color}44` }} />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Son familier</p>
              <p className="font-serif-display text-2xl">{g.familiar.name}</p>
              <p className="text-sm text-foreground/70">{g.familiar.title}</p>
            </div>
          </div>
        )}
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-8 mb-3">Activités qu'il ou elle inspire</p>
        <ul className="space-y-3">
          {g.activities.map((a) => (
            <li key={a} className="flex gap-3 text-foreground/80 leading-relaxed">
              <span className="mt-2 w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: g.color }} /> {a}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function GardiensGallery({ embedded = false }) {
  const [open, setOpen] = useState(null);
  return (
    <div data-testid="gardiens-gallery">
      <div className={`grain overflow-hidden ${embedded ? "rounded-3xl" : ""}`}><img src={POSTER} alt="Les Gardiens des Souvenirs et des forces intérieures" className="w-full max-h-[60vh] object-contain mx-auto block" /></div>
      <main className={embedded ? "py-10" : "max-w-6xl mx-auto px-6 py-14"}>
        {(() => { const H = embedded ? "h2" : "h1"; return <H className="font-serif-display italic font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">Un jardin de belles émotions</H>; })()}
        <p className="mt-6 text-lg text-foreground/70 max-w-2xl leading-relaxed">Chaque Gardien veille sur une émotion précieuse ou une force intérieure, et accompagne votre proche dans ses activités avec son fidèle compagnon.</p>
        {COLLECTIONS.map((c) => (
          <section key={c.id} className="mt-16" data-testid={`gardiens-collection-${c.id}`}>
            <p className="text-sm uppercase tracking-[0.3em] text-warm mb-3">{c.label}</p>
            <p className="text-foreground/70 max-w-3xl mb-8">{c.intro}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {GARDIENS.filter((g) => g.collection === c.id).map((g) => <GardienCard key={g.id} g={g} onOpen={setOpen} />)}
            </div>
          </section>
        ))}
        <section className="mt-20" data-testid="gardiens-familiers">
          <p className="text-sm uppercase tracking-[0.3em] text-warm mb-3">Les Familiers</p>
          <p className="text-foreground/70 max-w-3xl mb-8">Chacun des 17 Gardiens est accompagné d'un fidèle compagnon qui veille avec lui.</p>
          <img src={POSTERS.familiers} alt="Les Familiers des Gardiens" className="w-full rounded-3xl mb-8" />
          {COLLECTIONS.map((c) => (
            <div key={c.id} className="mb-10">
              <p className="font-serif-display text-2xl mb-4">{c.id === "souvenirs" ? "Compagnons des Gardiens des Souvenirs" : "Compagnons des Gardiens des Forces intérieures"}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
                {GARDIENS.filter((g) => g.familiar && g.collection === c.id).map((g) => (
                  <button key={g.id} onClick={() => setOpen(g)} data-testid={`familiar-card-${g.id}`} className="text-center rounded-3xl bg-card border border-border/60 p-4 hover:-translate-y-1 transition-transform duration-300">
                    <img src={g.familiar.image} alt={g.familiar.name} className="w-24 h-24 rounded-full object-cover mx-auto" style={{ boxShadow: `0 0 0 3px ${g.color}44` }} />
                    <p className="font-serif-display text-xl mt-3">{g.familiar.name}</p>
                    <p className="text-xs text-muted-foreground leading-snug">{g.familiar.title}</p>
                    <p className="text-xs mt-2 font-semibold" style={{ color: g.color }}>Compagnon {/^[AEIOUYÉÈ]/.test(g.name) ? `d'${g.name}` : `de ${g.name}`}</p>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </section>
      </main>
      {open && <GardienDetail g={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

export default function Gardiens() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background" data-testid="gardiens-page">
      <header className="bg-background/80 border-b border-border/60 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <img src={LOGO} alt="Passerelle" className="h-10 w-auto" />
          <Button variant="ghost" size="sm" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/espaces"))} data-testid="gardiens-back-button">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
        </div>
      </header>
      <GardiensGallery />
    </div>
  );
}
